import React from 'react';
import type { OverviewResponse, TestsResponse } from 'modules/analytics';
import type { PendingTestGroup } from '../analyticsFormat';
import { ErrorBlock } from './shared';
import { NeedsAttentionQueue } from './NeedsAttentionQueue';
import { SubmissionsBarChart } from './SubmissionsBarChart';
import { OutcomesPanel } from './OutcomesPanel';
import { DistributionPanel } from './DistributionPanel';
import { TestResultsTable } from './TestResultsTable';

interface Props {
  data?: OverviewResponse;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  pendingGroups: PendingTestGroup[];
  pendingLoading: boolean;
  integrityEvents: number;
  hasAdvanced: boolean;
  testsData?: TestsResponse;
  testsLoading: boolean;
  periodLabel: string;
  onReviewIntegrity: () => void;
  onShowAllTests: () => void;
}

/**
 * Action first (attention + daily submissions), then outcomes and the score
 * spread, then results per test. Funnel, spread and the per-test table come from
 * the PRO tests endpoint and show the upgrade notice on other plans.
 */
const OverviewTab: React.FC<Props> = ({
  data,
  isLoading,
  isError,
  errorMessage,
  pendingGroups,
  pendingLoading,
  integrityEvents,
  hasAdvanced,
  testsData,
  testsLoading,
  periodLabel,
  onReviewIntegrity,
  onShowAllTests,
}) => {
  if (isError) return <ErrorBlock message={errorMessage} />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <NeedsAttentionQueue
          pendingGroups={pendingGroups}
          integrityEvents={integrityEvents}
          onReviewIntegrity={onReviewIntegrity}
          isLoading={pendingLoading}
        />
        <SubmissionsBarChart activity={data?.recentActivity ?? []} isLoading={isLoading} periodLabel={periodLabel} />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <OutcomesPanel
          data={data}
          isLoading={isLoading}
          hasAdvanced={hasAdvanced}
          funnel={testsData?.funnel}
          funnelLoading={testsLoading}
        />
        <DistributionPanel distribution={testsData?.distribution} isLoading={testsLoading} hasAdvanced={hasAdvanced} />
      </div>

      <TestResultsTable
        tests={testsData?.tests}
        isLoading={testsLoading}
        hasAdvanced={hasAdvanced}
        onShowAll={onShowAllTests}
      />
    </div>
  );
};

export default OverviewTab;
